/**
 * TerraMatrix Data Service - Schema Groups API Service
 *
 * Admin API for managing Products (verticals), Schema Groups, and Organization assignments.
 * Connects to the data-service backend (port 8002).
 */

import { config as appConfig } from '@/config';
import { createDataServiceClient } from './data-service-client';
import type { DataServiceListingScope } from './data-service-client';
import type { AxiosRequestConfig } from 'axios';

const dataServiceClient = createDataServiceClient({ baseURL: appConfig.api.dataServiceUrl });

function getScopedRequestConfig(
  organizationId?: string | null,
): AxiosRequestConfig | undefined {
  if (typeof organizationId !== 'string' || organizationId.trim().length === 0) {
    return undefined;
  }

  return {
    headers: {
      'X-Organization-Id': organizationId.trim(),
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Safely extracts a string value from a potentially nested object.
 * Handles i18n objects like { en: "English", es: "Spanish" } by returning the first value.
 */
const safeString = (value: unknown, fallback = ''): string => {
  if (value === null || value === undefined) {
    return fallback;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (isRecord(value)) {
    // Handle i18n objects or similar - try common locale keys first
    if (typeof value.en === 'string') return value.en;
    if (typeof value.default === 'string') return value.default;
    // Fall back to the first string value found
    const values = Object.values(value);
    for (const v of values) {
      if (typeof v === 'string') return v;
    }
  }
  return fallback;
};

// ============================================================================
// Types
// ============================================================================

export type ProductStatus = 'active' | 'inactive';
export type ProductType = 'platform' | 'organization';
export type MembershipStatus = 'active' | 'deprecated';
export type StorageProvider = 'mongo' | 'vector' | 'timeseries';
export type SubscriptionStatus = 'active' | 'suspended' | 'expired' | 'trial';
export type SubscriptionTier = 'free' | 'basic' | 'pro' | 'enterprise';
export type AccessLevel = 'READ' | 'WRITE';
export type DataMode = 'SHARED' | 'ISOLATED';

// Organization assignment with access level
export interface OrganizationAssignment {
  organizationId: string;
  accessLevel: AccessLevel;
}

// Product Types
export interface Product {
  id: string;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  type: ProductType;
  organizationId?: string | null;
  isDefault: boolean;
  customDomain?: string;
  platformMeta?: {
    category?: string;
    tier?: string;
    featuredOrder?: number;
  };
  status: ProductStatus;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProductWithGroups extends Product {
  groups: SchemaGroup[];
}

export interface CreateProductInput {
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
}

export interface CreatePlatformProductInput {
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  customDomain?: string;
  platformMeta?: {
    category?: string;
    tier?: string;
    featuredOrder?: number;
  };
}

export interface CreateOrgProductInput {
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  isDefault?: boolean;
}

export interface UpdateProductInput {
  name?: string;
  description?: string;
  icon?: string;
  status?: ProductStatus;
}

// Product Subscription Types
export interface ProductSubscription {
  id: string;
  organizationId: string;
  productId: string;
  status: SubscriptionStatus;
  tier: SubscriptionTier;
  subscribedAt: string;
  expiresAt?: string | null;
  trialEndsAt?: string | null;
  permissions: {
    canViewTemplates: boolean;
    canUseSchemas: boolean;
    canAccessWorkflows: boolean;
    canCustomize: boolean;
  };
  product?: Product;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SubscribeInput {
  productId: string;
  tier?: SubscriptionTier;
  trialDays?: number;
}

export interface AccessibleProducts {
  ownProducts: Product[];
  subscribedProducts: Product[];
}

// Schema Group Types
export interface SchemaGroup {
  id: string;
  name: string;
  slug: string;
  description?: string;
  organizationId: string;
  productId?: string | null;
  dataMode?: DataMode;
  assignedOrganizations: OrganizationAssignment[];
  databaseConnectionId?: string | null;
  schemaCount?: number;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGroupInput {
  name: string;
  slug?: string;
  description?: string;
  productId?: string;
  assignedOrganizations?: string[];
  databaseConnectionId?: string;
}

export interface UpdateGroupInput {
  name?: string;
  description?: string;
  productId?: string | null;
  databaseConnectionId?: string | null;
}

export interface ListGroupsOptions {
  page?: number;
  limit?: number;
  search?: string;
  includeDeleted?: boolean;
  scope?: DataServiceListingScope;
}

// Schema Group Membership Types
export interface StorageConfig {
  primary: StorageProvider;
  retentionDays?: number;
  indexes?: Array<{
    fields: string[];
    options?: Record<string, unknown>;
  }>;
}

export interface SchemaGroupMembership {
  id: string;
  schemaId: string;
  groupId: string;
  organizationId: string;
  status: MembershipStatus;
  pinnedVersion?: number | null;
  collectionName: string;
  storageConfig: StorageConfig;
  databaseConnectionId?: string | null;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
  // Populated fields
  schema?: {
    id: string;
    name: string;
    displayName: string;
    dataType: string;
  };
}

export interface AddSchemaToGroupInput {
  schemaId: string;
  pinnedVersion?: number;
  storageConfig?: {
    primary?: StorageProvider;
    retentionDays?: number;
    indexes?: Array<{
      fields: string[];
      options?: Record<string, unknown>;
    }>;
  };
  databaseConnectionId?: string;
}

export interface DataServiceScopeOptions {
  organizationId?: string | null;
}

function getRecordValue(record: Record<string, unknown>, key: string): unknown {
  return Reflect.get(record, key);
}

function getNestedRecord(
  record: Record<string, unknown>,
  key: string,
): Record<string, unknown> | undefined {
  const value = getRecordValue(record, key);
  return isRecord(value) ? value : undefined;
}

function getMembershipStatus(value: unknown): MembershipStatus {
  return value === 'deprecated' ? 'deprecated' : 'active';
}

function getStorageProvider(value: unknown): StorageProvider {
  return value === 'vector' || value === 'timeseries' ? value : 'mongo';
}

function getPinnedVersion(value: unknown): number | null | undefined {
  if (typeof value === 'number') {
    return value;
  }

  return value === null ? null : undefined;
}

function getSchemaIdValue(rawMembership: Record<string, unknown>): string {
  const rawSchemaId = getRecordValue(rawMembership, 'schemaId');
  if (isRecord(rawSchemaId)) {
    return safeString(
      getRecordValue(rawSchemaId, '_id') ?? getRecordValue(rawSchemaId, 'id'),
    );
  }

  return safeString(rawSchemaId);
}

function getPopulatedSchema(
  rawMembership: Record<string, unknown>,
): SchemaGroupMembership['schema'] | undefined {
  const rawSchema =
    getNestedRecord(rawMembership, 'schema') ?? getNestedRecord(rawMembership, 'schemaId');

  if (!rawSchema) {
    return undefined;
  }

  return {
    id: safeString(getRecordValue(rawSchema, '_id') ?? getRecordValue(rawSchema, 'id')),
    name: safeString(getRecordValue(rawSchema, 'name')),
    displayName:
      safeString(getRecordValue(rawSchema, 'displayName')) ||
      safeString(getRecordValue(rawSchema, 'name')),
    dataType: safeString(getRecordValue(rawSchema, 'dataType'), 'object'),
  };
}

function buildSchemaGroupMembership(rawMembershipValue: unknown): SchemaGroupMembership {
  const rawMembership = isRecord(rawMembershipValue) ? rawMembershipValue : {};
  const storageConfig = getNestedRecord(rawMembership, 'storageConfig');

  return {
    id: String(getRecordValue(rawMembership, '_id') || getRecordValue(rawMembership, 'id') || ''),
    schemaId: getSchemaIdValue(rawMembership),
    groupId: String(getRecordValue(rawMembership, 'groupId') || ''),
    organizationId: String(getRecordValue(rawMembership, 'organizationId') || ''),
    status: getMembershipStatus(getRecordValue(rawMembership, 'status')),
    pinnedVersion: getPinnedVersion(getRecordValue(rawMembership, 'pinnedVersion')),
    collectionName: String(getRecordValue(rawMembership, 'collectionName') || ''),
    storageConfig: {
      primary: getStorageProvider(storageConfig?.primary),
      retentionDays:
        storageConfig?.retentionDays !== undefined ? Number(storageConfig.retentionDays) : 90,
    },
    databaseConnectionId: getRecordValue(rawMembership, 'databaseConnectionId')
      ? String(getRecordValue(rawMembership, 'databaseConnectionId'))
      : null,
    createdBy: String(getRecordValue(rawMembership, 'createdBy') || ''),
    updatedBy: getRecordValue(rawMembership, 'updatedBy')
      ? String(getRecordValue(rawMembership, 'updatedBy'))
      : undefined,
    createdAt: String(getRecordValue(rawMembership, 'createdAt') || ''),
    updatedAt: String(getRecordValue(rawMembership, 'updatedAt') || ''),
    schema: getPopulatedSchema(rawMembership),
  };
}

export interface ListGroupSchemasOptions {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  scope?: DataServiceListingScope;
}

export interface UpdateMembershipInput {
  pinnedVersion?: number | null;
  storageConfig?: {
    retentionDays?: number;
  };
}

// Schema Types (simplified for UI)
export interface Schema {
  id: string;
  name: string;
  displayName: string;
  dataType: string;
  status: string;
  currentVersion: number;
}

// Pagination Types
export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface PaginatedResult<T> {
  data: T[];
  pagination: PaginationInfo;
}

// Retention Presets
export const RETENTION_PRESETS = [
  { value: 7, label: '1 Week' },
  { value: 30, label: '1 Month' },
  { value: 90, label: '3 Months' },
  { value: 365, label: '1 Year' },
  { value: 1825, label: '5 Years' },
  { value: 0, label: 'Forever' },
] as const;

// ============================================================================
// API Functions
// ============================================================================

export const groupApi = {
  // =========================================================================
  // Product Management API (Admin only)
  // =========================================================================

  /**
   * List all products
   */
  listProducts: async (options?: {
    includeGroups?: boolean;
    includeInactive?: boolean;
  }): Promise<Product[] | ProductWithGroups[]> => {
    const params: Record<string, string> = {};
    if (options?.includeGroups) params.includeGroups = 'true';
    if (options?.includeInactive) params.includeInactive = 'true';
    const response = await dataServiceClient.get('/api/admin/products', { params });
    const rawProducts = response.data.data || response.data || [];
    // Normalize nested groups to expose `id` (the admin/products endpoint returns nested groups with `_id` only).
    // Without this, group.id is undefined when passed to dialogs, causing CastError in downstream API calls.
    return rawProducts.map((p: Record<string, unknown>) => {
      const groups = Array.isArray(p.groups) ? p.groups : null;
      if (!groups) return p;
      return {
        ...p,
        groups: groups.map((g: Record<string, unknown>) => ({
          ...g,
          id: safeString(g.id || g._id),
        })),
      };
    });
  },

  /**
   * Get a product by ID
   */
  getProduct: async (id: string, includeGroups = false): Promise<Product | ProductWithGroups> => {
    const params = includeGroups ? { includeGroups: 'true' } : {};
    const response = await dataServiceClient.get(`/api/admin/products/${id}`, { params });
    return response.data.data || response.data;
  },

  /**
   * Create a new product
   */
  createProduct: async (input: CreateProductInput): Promise<Product> => {
    const response = await dataServiceClient.post('/api/admin/products', input);
    return response.data.data || response.data;
  },

  /**
   * Update a product
   */
  updateProduct: async (id: string, input: UpdateProductInput): Promise<Product> => {
    const response = await dataServiceClient.put(`/api/admin/products/${id}`, input);
    return response.data.data || response.data;
  },

  /**
   * Delete a product (only if no groups)
   */
  deleteProduct: async (id: string): Promise<void> => {
    await dataServiceClient.delete(`/api/admin/products/${id}`);
  },

  /**
   * Activate a product
   */
  activateProduct: async (id: string): Promise<Product> => {
    const response = await dataServiceClient.post(`/api/admin/products/${id}/activate`);
    return response.data.data || response.data;
  },

  /**
   * Deactivate a product
   */
  deactivateProduct: async (id: string): Promise<Product> => {
    const response = await dataServiceClient.post(`/api/admin/products/${id}/deactivate`);
    return response.data.data || response.data;
  },

  // =========================================================================
  // Schema Group Management API
  // =========================================================================

  /**
   * List groups for the current organization
   */
  listGroups: async (options?: ListGroupsOptions): Promise<PaginatedResult<SchemaGroup>> => {
    const params: Record<string, string | number> = {};
    if (options?.page) params.page = options.page;
    if (options?.limit) params.limit = options.limit;
    if (options?.search) params.search = options.search;
    if (options?.includeDeleted) params.includeDeleted = 'true';
    params.scope = options?.scope ?? 'current';
    const response = await dataServiceClient.get('/api/groups', { params });
    return {
      data: response.data.data || [],
      pagination: response.data.pagination || {
        page: 1,
        limit: 20,
        total: (response.data.data || []).length,
        pages: 1,
      },
    };
  },

  /**
   * Get a group by ID
   */
  getGroup: async (id: string): Promise<SchemaGroup> => {
    const response = await dataServiceClient.get(`/api/groups/${id}`);
    return response.data.data || response.data;
  },

  /**
   * Get a group by slug
   */
  getGroupBySlug: async (slug: string): Promise<SchemaGroup> => {
    const response = await dataServiceClient.get(`/api/groups/slug/${slug}`);
    return response.data.data || response.data;
  },

  /**
   * Create a new group
   */
  createGroup: async (
    input: CreateGroupInput,
    options?: DataServiceScopeOptions,
  ): Promise<SchemaGroup> => {
    const response = await dataServiceClient.post(
      '/api/groups',
      input,
      getScopedRequestConfig(options?.organizationId),
    );
    return response.data.data || response.data;
  },

  /**
   * Update a group
   */
  updateGroup: async (id: string, input: UpdateGroupInput): Promise<SchemaGroup> => {
    const response = await dataServiceClient.put(`/api/groups/${id}`, input);
    return response.data.data || response.data;
  },

  /**
   * Delete a group (soft delete)
   */
  deleteGroup: async (id: string): Promise<void> => {
    await dataServiceClient.delete(`/api/groups/${id}`);
  },

  // =========================================================================
  // Schema Membership Management API
  // =========================================================================

  /**
   * Get schemas in a group
   */
  getGroupSchemas: async (
    groupId: string,
    includeDeprecated = false
  ): Promise<SchemaGroupMembership[]> => {
    const params = includeDeprecated ? { includeDeprecated: 'true' } : {};
    const response = await dataServiceClient.get(`/api/groups/${groupId}/schemas`, { params });
    const rawMemberships = response.data.data?.memberships || response.data.memberships || [];

    return Array.isArray(rawMemberships)
      ? rawMemberships.map((membership) => buildSchemaGroupMembership(membership))
      : [];
  },

  /**
   * Add schema to group
   */
  addSchemaToGroup: async (
    groupId: string,
    input: AddSchemaToGroupInput,
    options?: DataServiceScopeOptions,
  ): Promise<SchemaGroupMembership> => {
    const response = await dataServiceClient.post(
      `/api/groups/${groupId}/schemas`,
      input,
      getScopedRequestConfig(options?.organizationId),
    );
    return buildSchemaGroupMembership(response.data.data || response.data);
  },

  /**
   * Remove schema from group
   */
  removeSchemaFromGroup: async (groupId: string, schemaId: string): Promise<void> => {
    await dataServiceClient.delete(`/api/groups/${groupId}/schemas/${schemaId}`);
  },

  /**
   * Update schema membership (e.g., retention, pinned version)
   */
  updateMembership: async (
    groupId: string,
    schemaId: string,
    input: UpdateMembershipInput
  ): Promise<SchemaGroupMembership> => {
    const response = await dataServiceClient.patch(
      `/api/groups/${groupId}/schemas/${schemaId}`,
      input
    );
    return buildSchemaGroupMembership(response.data.data || response.data);
  },

  // =========================================================================
  // Organization Assignment API
  // =========================================================================

  /**
   * Get organizations assigned to a group (returns array of { organizationId, accessLevel })
   */
  getAssignedOrganizations: async (groupId: string): Promise<OrganizationAssignment[]> => {
    const response = await dataServiceClient.get(`/api/groups/${groupId}/organizations`);
    const orgs = response.data.data?.organizations || response.data.organizations || [];
    // Ensure we return OrganizationAssignment objects (handle legacy string format)
    return orgs.map((org: string | OrganizationAssignment) => {
      if (typeof org === 'string') {
        return { organizationId: org, accessLevel: 'READ' as AccessLevel };
      }
      return org;
    });
  },

  /**
   * Assign an organization to a group
   * @param accessLevel - READ or WRITE (defaults to READ)
   */
  assignOrganization: async (
    groupId: string,
    organizationId: string,
    accessLevel: AccessLevel = 'READ'
  ): Promise<SchemaGroup> => {
    const response = await dataServiceClient.post(`/api/groups/${groupId}/organizations`, {
      organizationId,
      accessLevel,
    });
    return response.data.data || response.data;
  },

  /**
   * Update access level for an assigned organization
   */
  updateOrganizationAccess: async (
    groupId: string,
    organizationId: string,
    accessLevel: AccessLevel
  ): Promise<SchemaGroup> => {
    const response = await dataServiceClient.patch(
      `/api/groups/${groupId}/organizations/${organizationId}`,
      { accessLevel }
    );
    return response.data.data || response.data;
  },

  /**
   * Remove an organization from a group
   */
  removeOrganization: async (groupId: string, organizationId: string): Promise<void> => {
    await dataServiceClient.delete(`/api/groups/${groupId}/organizations/${organizationId}`);
  },

  /**
   * Replace all assigned organizations for a group
   * @param organizations - Array of { organizationId, accessLevel } objects
   */
  setAssignedOrganizations: async (
    groupId: string,
    organizations: OrganizationAssignment[]
  ): Promise<SchemaGroup> => {
    const response = await dataServiceClient.put(`/api/groups/${groupId}/organizations`, {
      organizations,
    });
    return response.data.data || response.data;
  },

  // =========================================================================
  // Schema API (for listing available schemas)
  // =========================================================================

  /**
   * List all schemas for the organization
   */
  listSchemas: async (options?: ListGroupSchemasOptions): Promise<Schema[]> => {
    const params: Record<string, string | number> = {};
    if (options?.page) params.page = options.page;
    if (options?.limit) params.limit = options.limit;
    if (options?.search) params.search = options.search;
    if (options?.status) params.status = options.status;
    params.scope = options?.scope ?? 'current';
    const response = await dataServiceClient.get('/api/schemas', { params });
    const rawData = response.data.data || response.data.schemas || [];

    // Transform backend schema format to frontend format
    // Use safeString() to handle ObjectIds, i18n objects, and ensure all values are proper strings
    return rawData.map((schema: Record<string, unknown>) => ({
      id: safeString(schema._id || schema.id),
      name: safeString(schema.name),
      displayName: safeString(schema.displayName) || safeString(schema.name),
      dataType: safeString(schema.dataType, 'object'),
      status: safeString(schema.status, 'draft'),
      currentVersion: Number(schema.currentVersion) || 1,
    }));
  },

  /**
   * Get a schema by ID
   */
  getSchema: async (id: string): Promise<Schema> => {
    const response = await dataServiceClient.get(`/api/schemas/${id}`);
    return response.data.data || response.data;
  },

  // =========================================================================
  // Platform Products API
  // =========================================================================

  /**
   * List all platform products (marketplace)
   */
  listPlatformProducts: async (options?: {
    search?: string;
    category?: string;
  }): Promise<Product[]> => {
    const params: Record<string, string> = {};
    if (options?.search) params.search = options.search;
    if (options?.category) params.category = options.category;
    const response = await dataServiceClient.get('/api/admin/products/platform', { params });
    return response.data.data || response.data;
  },

  /**
   * List products accessible to the current organization
   * Returns org's own products + subscribed platform products
   */
  listAccessibleProducts: async (): Promise<AccessibleProducts> => {
    const response = await dataServiceClient.get('/api/admin/products/accessible');
    return response.data.data || response.data;
  },

  /**
   * List products belonging to the current organization
   */
  listOrgProducts: async (options?: {
    search?: string;
    includeInactive?: boolean;
  }): Promise<Product[]> => {
    const params: Record<string, string> = {};
    if (options?.search) params.search = options.search;
    if (options?.includeInactive) params.includeInactive = 'true';
    const response = await dataServiceClient.get('/api/admin/products/org', { params });
    return response.data.data || response.data;
  },

  /**
   * Get or create the default product for the current organization
   */
  getDefaultProduct: async (): Promise<Product> => {
    const response = await dataServiceClient.get('/api/admin/products/default');
    return response.data.data || response.data;
  },

  /**
   * Create a platform product (admin only)
   */
  createPlatformProduct: async (input: CreatePlatformProductInput): Promise<Product> => {
    const response = await dataServiceClient.post('/api/admin/products/platform', input);
    return response.data.data || response.data;
  },

  /**
   * Create an organization product
   */
  createOrgProduct: async (input: CreateOrgProductInput): Promise<Product> => {
    const response = await dataServiceClient.post('/api/admin/products/org', input);
    return response.data.data || response.data;
  },

  // =========================================================================
  // Subscription API
  // =========================================================================

  /**
   * List all subscriptions for the current organization
   */
  listSubscriptions: async (includeExpired = false): Promise<ProductSubscription[]> => {
    const params = includeExpired ? { includeExpired: 'true' } : {};
    const response = await dataServiceClient.get('/api/subscriptions', { params });
    return response.data.data || response.data;
  },

  /**
   * Subscribe to a platform product
   */
  subscribe: async (input: SubscribeInput): Promise<ProductSubscription> => {
    const response = await dataServiceClient.post('/api/subscriptions', input);
    return response.data.data || response.data;
  },

  /**
   * Start a trial subscription
   */
  startTrial: async (productId: string, trialDays = 14): Promise<ProductSubscription> => {
    const response = await dataServiceClient.post('/api/subscriptions/trial', {
      productId,
      trialDays,
    });
    return response.data.data || response.data;
  },

  /**
   * Check if organization has active subscription to a product
   */
  checkSubscription: async (productId: string): Promise<{
    hasAccess: boolean;
    subscription: ProductSubscription | null;
  }> => {
    const response = await dataServiceClient.get(`/api/subscriptions/check/${productId}`);
    return response.data.data || response.data;
  },

  /**
   * Get subscription details for a specific product
   */
  getSubscription: async (productId: string): Promise<ProductSubscription | null> => {
    const response = await dataServiceClient.get(`/api/subscriptions/${productId}`);
    return response.data.data || response.data;
  },

  /**
   * Cancel subscription to a product
   */
  unsubscribe: async (productId: string): Promise<void> => {
    await dataServiceClient.delete(`/api/subscriptions/${productId}`);
  },

  /**
   * Upgrade subscription tier
   */
  upgradeSubscription: async (
    productId: string,
    tier: SubscriptionTier
  ): Promise<ProductSubscription> => {
    const response = await dataServiceClient.post(`/api/subscriptions/${productId}/upgrade`, {
      tier,
    });
    return response.data.data || response.data;
  },
};

export default groupApi;
